import type { StatName } from './deeds.js';

/**
 * Уровни и очки статов. Кривая из документа: xp_до_уровня(L) = 80 × L^1.5,
 * то есть десятый уровень стоит около 2 500 опыта, тридцатый — около 13 100.
 */

export const STAT_POINTS_PER_LEVEL = 2;
export const CLASS_LEVEL = 5;
export const SUBCLASS_LEVEL = 15;

export function xpForLevel(level: number): number {
  if (level <= 1) return 0;
  return Math.round(80 * Math.pow(level, 1.5));
}

/** Уровень по накопленному опыту. */
export function levelFromXp(xp: number): number {
  let level = 1;
  while (xp >= xpForLevel(level + 1)) level += 1;
  return level;
}

/** Сколько опыта до следующего уровня и сколько всего нужно на него. */
export function xpProgress(xp: number): { level: number; into: number; need: number } {
  const level = levelFromXp(xp);
  const current = xpForLevel(level);
  const next = xpForLevel(level + 1);
  return { level, into: xp - current, need: next - current };
}

/**
 * Опыт за дело. Считается от денег и сложности, а не от времени:
 * иначе выгоднее всего было бы часами вскрывать самое дешёвое.
 */
export function jobXp(opts: { difficulty: number; payout?: number; lootValue?: number }): number {
  const money = (opts.payout ?? 0) + (opts.lootValue ?? 0);
  return Math.max(1, Math.round(1 + money / 5000 + opts.difficulty));
}

/** Очки статов за подъём с одного уровня на другой. */
export function statPointsFor(fromLevel: number, toLevel: number): number {
  return Math.max(0, (toLevel - fromLevel) * STAT_POINTS_PER_LEVEL);
}

export interface ClassBonus {
  /** Ветка дел, к которой класс приложим. */
  branch?: string;
  /** Множитель выплаты по своей ветке. */
  payoutFactor: number;
  /** Множитель времени по своей ветке. */
  minutesFactor: number;
  /** Множитель палева по своей ветке: угонщик работает тише. */
  heatFactor: number;
  /** Плоские прибавки к статам и здоровью. */
  statBonus?: Partial<Record<StatName, number>>;
  healthBonus?: number;
  /** Дополнительные слоты спутниц. */
  companionSlots?: number;
}

export const NO_CLASS_BONUS: ClassBonus = {
  payoutFactor: 1,
  minutesFactor: 1,
  heatFactor: 1,
};

/** Складывает бонусы класса и подкласса в один множитель. */
export function combineClassBonuses(bonuses: readonly ClassBonus[]): ClassBonus {
  return bonuses.reduce<ClassBonus>(
    (acc, bonus) => ({
      branch: bonus.branch ?? acc.branch,
      payoutFactor: acc.payoutFactor * bonus.payoutFactor,
      minutesFactor: acc.minutesFactor * bonus.minutesFactor,
      heatFactor: acc.heatFactor * bonus.heatFactor,
      statBonus: mergeStats(acc.statBonus, bonus.statBonus),
      healthBonus: (acc.healthBonus ?? 0) + (bonus.healthBonus ?? 0),
      companionSlots: (acc.companionSlots ?? 0) + (bonus.companionSlots ?? 0),
    }),
    { ...NO_CLASS_BONUS, statBonus: {}, healthBonus: 0, companionSlots: 0 },
  );
}

function mergeStats(
  a: Partial<Record<StatName, number>> | undefined,
  b: Partial<Record<StatName, number>> | undefined,
): Partial<Record<StatName, number>> {
  const out: Partial<Record<StatName, number>> = { ...a };
  for (const [stat, value] of Object.entries(b ?? {})) {
    const key = stat as StatName;
    out[key] = (out[key] ?? 0) + (value ?? 0);
  }
  return out;
}
